import { FirebaseError, Unsubscribe } from "@firebase/util";
import {
  EContractTab,
  ELeftSideMenu,
  ETechnicalRetentionType,
} from "api/enums";
import { IContractData, ICustomPropsDefinitions } from "api/interfaces";
import FileService from "api/services/FileService";
import { isEqual } from "lodash";
import { makeAutoObservable } from "mobx";
import { uuidFirebase } from "utils";
import { decimal } from "utils/DecimalPrecision";
import {
  ContractAttachmentStore,
  ContractRowStore,
  IndicatorsStore,
  ContractIntegrationStore as Integration,
  MeasurementListStore,
  MessageStore,
} from "../";
import ContractModel from "../../api/models/ContractModel";
import UserModel from "../../api/models/UserModel";
import AuthService from "../../api/services/AuthService";
import ContractService from "../../api/services/ContractService";
import UserService from "../../api/services/UserService";
import { lang } from "../../i18nLang";

/**
 * Store/Controller de contratos
 * @category Contracts
 * @hideconstructor
 */

class Store {
  /** Referência para o snapshot que observa as modificações no contrato atual. */
  private _currentContractListener: Unsubscribe | undefined = undefined;
  /** Contrato atual que foi aberto pelo usuário. */
  private _currentContract: ContractModel | undefined = undefined;
  /** Total do contrato atual. */
  public currentContractTotal: number = 0;
  /** Lista as definições das propriedades customizadas do contrato. */
  public customPropertiesList: ICustomPropsDefinitions[] = [];
  /** Propriedade utilizada apenas para sinalizar os observadores sobre alguma atualização no contrato atual. */
  public flagCurrentContractChanged: boolean = false;
  /** Flag que sinaliza se o menu está aberto e qual tela deve ser aberta nele. */
  public openLeftSideMenu: ELeftSideMenu = ELeftSideMenu.CLOSED;
  /** Flag que controla qual aba da tela de contrato é exibida. */
  public currentTabOpened: EContractTab = EContractTab.CONTRACT;
  /** Guarda o id do Contrato a ser criado. */
  private _createdContractUid: string = "";

  constructor() {
    makeAutoObservable(this);
  }

  private reset() {
    this._currentContractListener = undefined;
    this._currentContract = undefined;
    this.currentContractTotal = 0;
    this.customPropertiesList = [];
    this.flagCurrentContractChanged = false;
    this.openLeftSideMenu = ELeftSideMenu.CLOSED;
    this.currentTabOpened = EContractTab.CONTRACT;
    this._createdContractUid = "";
  }

  /** Retorna a referência para o contrato ATUAL. */
  public getCurrentContract(): ContractModel | undefined {
    return this._currentContract;
  }

  /** Retorna o ID do contrato ATUAL ou do contrato
   * a ser criado.
   */
  public getCurrentOrCreatedContractId(): string {
    if (this._currentContract) {
      return this._currentContract.uid;
    }
    return this._createdContractUid;
  }

  /** Altera a flag que sinaliza qual tela deve ser aberta no menu
   * para o menu de "Aditivos e Distratos". */
  public openAddsAndCancelsMenu() {
    this.openLeftSideMenu = ELeftSideMenu.ADDS_AND_CANCELS;
  }

  /** Altera a flag que sinaliza qual tela deve ser aberta no menu
   * para o menu de "Detalhes" da linha. */
  public openLineHistoryMenu() {
    this.openLeftSideMenu = ELeftSideMenu.LINE_HISTORY;
  }

  /** Altera a flag que sinaliza qual tela deve ser aberta no menu
   * para o menu de "Reajuste". */
  public openReadjustment() {
    this.openLeftSideMenu = ELeftSideMenu.READJUSTMENT;
  }

  /** Altera a flag para sinalizar que o menu está fechado. */
  public closeLeftSideMenu() {
    this.openLeftSideMenu = ELeftSideMenu.CLOSED;
    ContractRowStore.unselectRows();
  }

  /** Altera qual aba da tela do contrato é aberta.  */
  public setCurrentTabOpened(tabIndex: EContractTab) {
    this.currentTabOpened = tabIndex;
  }

  /** Inscreve um snapshot no contrato a ser aberto e executa as rotinas necessárias
   * para apresentar todos dados necessários do contrato, como suas propriedades customizadas,
   * suas linhas e medições. */
  public async subscribeContract(
    uid: string,
    onFinish: Function,
    onError: Function
  ) {
    this.unsubscribeContract();
    const observer = await ContractService.subscribeContract(
      uid,
      (contract) => {
        this.setCurrentContract(contract);
        if (contract) {
          this.setCustomProperties();
          ContractRowStore.subscribeContractRows();
          MeasurementListStore.subscribeMeasurements();
        }

        onFinish(contract);
      },
      (contract) => {
        if (contract) {
          this._currentContract?.copyFrom(contract);
          this.setCustomProperties();
          this.updateCurrentContractTotal();
          this.signalContractChanges();
        } else {
          this.setCurrentContract(undefined);
        }
      },
      onError
    );

    if (observer) {
      this._currentContractListener = observer;
    }
  }

  /** Obtém as definições de propriedades customizadas do contrato. */
  private setCustomProperties() {
    const contract = this._currentContract;
    if (!contract) return;

    ContractService.getConstractsCustomProperties(contract).then((props) => {
      if (!isEqual(props, contract.customProperties)) {
        contract.setCustomProperties(props);
        this.customPropertiesList = [...props];
      }
    });
  }

  /** Desinscreve o snapshot do contrato, de suas linhas e de suas medições e depois
   * reset as varíaveis desta Store. */
  public unsubscribeContract() {
    if (this._currentContractListener) {
      this._currentContractListener();
      ContractRowStore.unsubscribeContractRows();
      MeasurementListStore.unscribeMeasurements();
    }
    this.reset();
  }

  public async getCurrentIntegrationData(): Promise<
    Partial<IContractData> | undefined
  > {
    const contract = this._currentContract;
    await Integration.getDataToContract();

    if (contract) {
      const branch = Integration.findBranch(contract.branchId || 0);
      const paymentCondition = Integration.findPaymentCondition(contract);
      const documentType = Integration.findDocumentType(contract);
      const costCenter = Integration.findCostCenter(contract);
      const projectErp = Integration.findProjectErp(contract);
      const indicator = Integration.findFinancialIndicator(
        contract.financialIndicator?.id || 0
      );

      return {
        branch,
        paymentCondition,
        documentType,
        costCenter,
        projectErp,
        indicator,
      };
    }

    return;
  }

  public async saveContract(data: IContractData, isEditing: boolean = true) {
    const contractData = { ...data };
    if (isEditing) {
      await this.updateContract(contractData);
    } else {
      await this.createContract(contractData);
    }
    await ContractAttachmentStore.saveContractAttachments();
  }

  /** Cria um novo contrato no banco de dados com os dados informados
   * por parâmetros e define o usuário atual como proprietário.
   * Ao final adiciona o novo contrato na lista de contratos do usuário.
   */
  private async createContract(data: IContractData): Promise<void> {
    const newId = this._createdContractUid;
    const contract = ContractModel.create(newId, {
      ...data,
      development: {
        name: data.projectName,
        customer: data.client?.name || "",
        local: data.location,
        customerId: data.client?.id || 0,
      },
      name: data.contractName,
      branchId: data.branch?.id,
      paymentCondition: createOption(data.paymentCondition),
      documentType: createOption(data.documentType),
      costCenter: createOption(data.costCenter),
      projectErp: createOption(data.projectErp),
      financialIndicator: data.indicator && { ...data.indicator },
      date: data.contractDate,
      managementType: data.managementType,
    });

    await ContractService.createContract(contract).catch((error) => {
      console.error(error);
      MessageStore.error(error.messages?.long || error.message);
    });
  }

  /** Atualiza os dados do contrato ATUAL no banco de dados executando todas
   * as validações necessárias.
   */
  private async updateContract(data: IContractData): Promise<void> {
    const contract = this._currentContract;
    if (!contract) return;

    const updatedData = {
      development: {
        name: data.projectName,
        customer: data.client?.name || "",
        local: data.location,
        customerId: data.client?.id || 0,
      },
      name: data.contractName,
      branchId: data.branch?.id,
      paymentCondition: createOption(data.paymentCondition),
      documentType: createOption(data.documentType),
      costCenter: createOption(data.costCenter),
      projectErp: createOption(data.projectErp),
      financialIndicator: data.indicator && { ...data.indicator },
      managementType: data.managementType,
      status: data.status,
    };

    await ContractService.updateContract(contract, updatedData).catch(
      (error) => {
        console.error(error);
        MessageStore.error(error.messages?.long || error.message);
      }
    );
  }

  /** Deleta o contrato ATUAL do banco de dados. */
  public async deleteContract() {
    const contract = this._currentContract;
    if (!contract) return false;

    MessageStore.toggleLoading();
    return ContractService.deleteContract(contract)
      .then(() => {
        this.reset();
        MessageStore.toggleLoading();
        MessageStore.success(lang("contract.deleteSuccess"));

        return true;
      })
      .catch((error) => {
        MessageStore.toggleLoading();

        if (
          error instanceof FirebaseError &&
          error.code === "storage/object-not-found"
        ) {
          const path = FileService.getPathFromUrl(contract.coverImageUrl || "");
          MessageStore.error(
            lang("exceptions.contract.coverNotFound", path),
            true
          );
        } else {
          MessageStore.error(error.message, true);
        }

        return false;
      });
  }

  /** Obtém os dados dos usuários que possuem acesso ao contrato. */
  public async getContractUsers(
    contract: ContractModel | undefined
  ): Promise<UserModel[]> {
    return contract ? UserService.getUsersByUid(contract.sharedUsersIds) : [];
  }

  /** Retorna se o usuário proprietário do contrato é o
   * usuário logado.
   */
  public userIsOwnerOf(contract: ContractModel | undefined): boolean {
    return contract
      ? contract.ownerUserId === AuthService.currentUserId
      : false;
  }

  /** Compartilha o contrato com os usuários válidos dos e-mails informados.
   * Atualiza a propriedade users do contrato com os IDs dos usuários que foram validados.
   */
  public async shareContratWithUsers(
    contract: ContractModel | undefined,
    emails: string[]
  ): Promise<boolean> {
    if (!contract) return false;
    if (!emails || !emails.length) return false;

    const users = await UserService.getUsersByEmail(emails);
    const usersIds = users.map((user) => user.uid);

    return ContractService.updateSharedUsers(contract, usersIds)
      .then(() => {
        MessageStore.success(lang("kanban.shareModal.success"));
        return true;
      })
      .catch((error) => {
        MessageStore.error(error.message);
        return false;
      });
  }

  /** Altera os dados da retenção técnica do contrato ATUAL. */
  public async updateTechnicalRetention(
    percentual: number,
    baseCalculation: string
  ): Promise<boolean> {
    const contract = this._currentContract;
    if (!contract) return false;

    return ContractService.updateTechnicalRetention(contract.uid, {
      percentual,
      type: baseCalculation as ETechnicalRetentionType,
    })
      .then(() => {
        MessageStore.success(lang("retention.success"));
        return false;
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(error.customMessage || error.message);
        return true;
      });
  }

  /** Atualiza os dados de Orçamento Executivo no contrato e
   * executa a rotina para atualizar obter os dados do orçamento
   * selecionado da API Mega.
   */
  public async updateExecutiveBudgetAndRefreshIndicators(data: {
    branchId: number;
    budgetId: string;
  }): Promise<void> {
    return this.updateExecutiveBudget(data).then(() => {
      IndicatorsStore.getExecutiveTotalsIfAuthenticated();
    });
  }

  /** Atualiza os dados de Orçamento Executivo no contrato ATUAL. */
  private async updateExecutiveBudget({
    branchId,
    budgetId,
  }: {
    branchId: number;
    budgetId: string;
  }): Promise<void> {
    const contract = this._currentContract;
    if (!contract) return;

    return ContractService.updateExecutiveBudget(contract, branchId, budgetId)
      .then((type) => {
        const message = lang(`contract.executiveBudget.${type}`);
        MessageStore.success(message);
        return;
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(lang("contract.executiveBudget.error"));
      });
  }

  public async updateContractMeasuredTotal(
    measurementsTotal: number,
    billingTotal: number
  ) {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    if (
      decimal(contract.measuredTotal).isEqualTo(measurementsTotal) &&
      decimal(contract.billingTotal).isEqualTo(billingTotal)
    ) {
      return;
    }

    return ContractService.updateMeasuredTotals(
      contract,
      measurementsTotal,
      billingTotal
    );
  }

  public async uploadContractCover(contract: ContractModel, file: File) {
    const path = `images/contract/${contract.uid + file.name}`;
    const newPath = await FileService.upload(file, path);
    if (newPath) {
      await ContractService.upadateCoverImage(contract, newPath);
    }
  }

  /** Define o contrato atual na Store. */
  private setCurrentContract(contract?: ContractModel) {
    this._currentContract = contract ? contract : undefined;
    this.updateCurrentContractTotal();
  }

  public validateContractDate(date?: Date) {
    if (date) {
      const now = new Date();
      if (date.getTime() <= now.getTime()) {
        return;
      }
    }

    return {
      valid: false,
      text: lang("exceptions.contract.invalidDate"),
    };
  }

  /** Gera o ID do contrato a ser criado. */
  public generateAndSetCreatedContractId() {
    const newId = uuidFirebase();
    this._createdContractUid = newId;
  }

  public clearCreatedContractId() {
    this._createdContractUid = "";
  }

  /** Troca o valor da flag 'flagCurrentContractChanged' de verdadeiro para falso e vice-versa. */
  private signalContractChanges() {
    this.flagCurrentContractChanged = !this.flagCurrentContractChanged;
  }

  /** Define o total do contrato atual na Store. */
  private updateCurrentContractTotal() {
    this.currentContractTotal = this._currentContract?.total || 0;
  }
}

function createOption(data?) {
  if (!data) return;
  return { id: data.id, code: data.code };
}

const ContractStore = new Store();
export default ContractStore;
